// Köken bandı + çip filtreleri — Geçmiş Rapor Aktarımı (Task 15, 8A/15A/17A).
//
// FormFill header'ının hemen altında tek satır: projeyi YARATAN aktarımın dosyası +
// sayıları (`bandImported`), izinle "raporu aç", gerekirse `fallbackNote`; altında
// dört çip (17A `<fieldset><legend>` + `aria-pressed`, 0 sayılı çip `aria-disabled`
// ama kaybolmaz — 11A). Bant auto-height (2A) — içerik sarabilir, sabit yükseklik yok.
//
// 8A durum ayrımı: `provenance === null` → HİÇBİR ŞEY çizilmez (aktarım yok ya da
// henüz yüklenmedi); `provenanceError` → tek muted satır + "Tekrar dene". Bu ikisi
// AYNI şey değildir.
//
// D13 — bu bileşen `<select>` İÇERMEZ (qc-flow.spec.ts Review'daki select sayısına
// bakıyor; köken bandı Review'a da giriyor olsaydı sayı kayardı — kural burada da
// korunur, filtre native butonlardır).

import { useEffect, useState } from "react";
import type { ProjectProvenanceResponse, ReportImportDetail } from "@mahir/shared";
import { getImport } from "../../api/report-import";
import { fillTemplate } from "../../lib/period-ui";
import {
  IMPORT_COPY,
  fileOpenUrl,
  latestCreateImport,
  possessiveSuffixTr,
  type ImportQuestionFilter,
} from "../../lib/report-import-ui";

export interface ProvenanceBarProps {
  provenance: ProjectProvenanceResponse | null;
  provenanceError: boolean;
  onReload: () => void;
  filter: ImportQuestionFilter;
  onFilterChange: (filter: ImportQuestionFilter) => void;
  /** Canlı çip sayıları (11A — çözülen reddedilenler düşer). */
  counts: { low: number; partial: number; rejected: number; empty: number };
  /** Projedeki görünür soru sayısı — banttaki `{total}`. */
  totalQuestions: number;
  /** `projects.import_report` — link + aktarım detayı (fallbackNote) bu izinle gelir. */
  canOpenReport: boolean;
}

function chipStyle(selected: boolean, muted: boolean): React.CSSProperties {
  const base: React.CSSProperties = {
    borderRadius: 999,
    padding: "4px 12px",
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.4,
    cursor: muted ? "default" : "pointer",
    opacity: muted ? 0.6 : 1,
    display: "inline-flex",
    alignItems: "center",
    color: "var(--color-text-2)",
    background: "color-mix(in srgb, var(--color-primary) 6%, white)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 25%, transparent)",
  };
  if (selected) {
    base.border = "2px solid var(--color-primary)";
    base.background = "color-mix(in srgb, var(--color-primary) 10%, white)";
    base.padding = "3px 11px";
  }
  return base;
}

export function ProvenanceBar(props: ProvenanceBarProps): JSX.Element | null {
  const {
    provenance,
    provenanceError,
    onReload,
    filter,
    onFilterChange,
    counts,
    totalQuestions,
    canOpenReport,
  } = props;
  const createImport = provenance ? latestCreateImport(provenance.imports) : null;
  const importId = createImport?.id ?? null;
  const [detail, setDetail] = useState<ReportImportDetail | null>(null);

  // `fallbackNote` (8A) yalnız aktarım detayında var (provenance yanıtı stats taşımaz).
  // Detay route'u `projects.import_report` ister — izni olmayan (ör. writer) kullanıcıda
  // hiç denenmez, bant sayıları zaten `entries`'ten türetilir.
  useEffect(() => {
    if (!importId || !canOpenReport) {
      setDetail(null);
      return;
    }
    let aborted = false;
    getImport(importId)
      .then((res) => {
        if (!aborted) setDetail(res.import);
      })
      .catch(() => {
        if (!aborted) setDetail(null);
      });
    return () => {
      aborted = true;
    };
  }, [importId, canOpenReport]);

  if (provenanceError) {
    return (
      <div
        role="status"
        className="flex items-center gap-2"
        style={{
          padding: "8px 24px",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
          fontSize: "0.78rem",
          color: "var(--color-text-2)",
        }}
      >
        <span>{IMPORT_COPY.provenanceUnavailable}</span>
        <button
          type="button"
          onClick={onReload}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: "var(--color-primary)",
            textDecoration: "underline",
            cursor: "pointer",
            font: "inherit",
          }}
        >
          {IMPORT_COPY.retryLoad}
        </button>
      </div>
    );
  }

  if (!provenance || !createImport) return null;

  // Bant sayıları aktarım ANINI anlatır (statik) — çipler canlıdır (11A).
  const accepted = provenance.entries.filter((e) => !e.rejected);
  const lowAtImport = accepted.filter((e) => e.confidence < provenance.threshold).length;
  const rejectedAtImport = provenance.entries.filter((e) => e.rejected).length;

  const chips: { key: ImportQuestionFilter; label: string; count: number | null }[] = [
    { key: "all", label: IMPORT_COPY.chipAll, count: null },
    { key: "low", label: fillTemplate(IMPORT_COPY.chipLow, { n: counts.low }), count: counts.low },
    // P6 (inceleme C3) — "Kısmi" çipi YALNIZ kısmi kabul varken çıkar. 17A "0 sayılı çip
    // kaybolmaz" kuralının bilinçli istisnası: kısmi kabul olmayan projelerde (bugünkü
    // tüm projeler + e2e dummy yolu) çip listesi birebir eskisi gibi kalsın.
    ...(counts.partial > 0
      ? [
          {
            key: "partial" as const,
            label: fillTemplate(IMPORT_COPY.chipPartial, { n: counts.partial }),
            count: counts.partial,
          },
        ]
      : []),
    {
      key: "rejected",
      label: fillTemplate(IMPORT_COPY.chipRejected, { n: counts.rejected }),
      count: counts.rejected,
    },
    {
      key: "empty",
      label: fillTemplate(IMPORT_COPY.chipEmpty, { n: counts.empty }),
      count: counts.empty,
    },
  ];

  return (
    <div
      style={{
        padding: "8px 24px",
        borderBottom: "1px solid var(--color-border)",
        background: "var(--color-surface)",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div className="min-w-0">
        {/* 15A — 14 px altı metin --color-text-2. */}
        <div style={{ fontSize: "0.8rem", color: "var(--color-text-2)" }}>
          {fillTemplate(IMPORT_COPY.bandImported, {
            file: createImport.fileName,
            answered: accepted.length,
            total: totalQuestions,
            low: lowAtImport,
            // S6 — Türkçe iyelik eki sayıdan türer (3 → "3'ü", 7 → "7'si").
            lowSuffix: possessiveSuffixTr(lowAtImport),
            rejected: rejectedAtImport,
          })}{" "}
          {canOpenReport ? (
            <a
              href={fileOpenUrl(createImport.fileUrl, createImport.fileKind)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--color-primary)",
                textDecoration: "underline",
                fontWeight: 600,
              }}
            >
              {IMPORT_COPY.bandOpenReport}
            </a>
          ) : null}
        </div>
        {detail?.stats?.fallbackToPdf ? (
          <div style={{ fontSize: "0.72rem", color: "var(--color-text-2)" }}>
            {IMPORT_COPY.fallbackNote}
          </div>
        ) : null}
      </div>

      <fieldset style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
        <legend
          style={{
            padding: 0,
            fontSize: 12,
            color: "var(--color-text-2)",
            marginBottom: 4,
          }}
        >
          {IMPORT_COPY.chipsLegend}
        </legend>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {chips.map((c) => {
            // 17A/11A — 0 sayılı çip KAYBOLMAZ, aria-disabled + %60 opaklıkla durur.
            const muted = c.count === 0;
            return (
              <button
                key={c.key}
                type="button"
                aria-pressed={filter === c.key}
                aria-disabled={muted || undefined}
                onClick={() => {
                  if (muted) return;
                  onFilterChange(c.key);
                }}
                style={chipStyle(filter === c.key, muted)}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}
